import { CareerOrientationGroupWithQuestions } from '@vsat/shared/lib/api';

export type ScoreEntry = { id: string; score: number; name: string };
export type TopGroupEntry = { id: string; score: number; name: string };

export type PositionedGroup = CareerOrientationGroupWithQuestions & {
  code: string;
  color: string;
  shortName: string;
  score: {
    id: string;
    score: number;
    name: string;
    actualScore: number;
    maxScore: number;
    percentage: number;
  };
  position: number;
  isTop3: boolean;
};

export type TopCode = {
  code: string;
  name: string;
  color: string;
  shortName: string;
  group: TopGroupEntry;
};

// Holland Code mapping - matching HTML design names
export const hollandCodeMap: Record<
  string,
  { code: string; name: string; color: string; shortName: string }
> = {
  'Thực tế & Kỹ thuật': { code: 'R', name: 'Realistic', color: 'gray', shortName: 'Kỹ thuật' },
  'Nghiên cứu & Khám phá': {
    code: 'I',
    name: 'Investigative',
    color: 'blue',
    shortName: 'Nghiên cứu',
  },
  'Nghệ thuật & Sáng tạo': {
    code: 'A',
    name: 'Artistic',
    color: 'orange',
    shortName: 'Nghệ thuật',
  },
  'Xã hội & Cộng đồng': { code: 'S', name: 'Social', color: 'green', shortName: 'Xã hội' },
  'Quản lý & Kinh doanh': {
    code: 'E',
    name: 'Enterprising',
    color: 'purple',
    shortName: 'Quản lý',
  },
  'Nghiệp vụ & Chi tiết': { code: 'C', name: 'Conventional', color: 'red', shortName: 'Nghiệp vụ' },
};

export const colorMap: Record<string, { bg: string; light: string; dark: string }> = {
  gray: { bg: 'from-gray-500/80 to-gray-600', light: 'bg-gray-100', dark: 'bg-gray-900/30' },
  blue: { bg: 'from-blue-500/80 to-blue-600', light: 'bg-blue-100', dark: 'bg-blue-900/30' },
  orange: {
    bg: 'from-orange-500/80 to-orange-600',
    light: 'bg-orange-100',
    dark: 'bg-orange-900/30',
  },
  green: { bg: 'from-green-500/80 to-green-600', light: 'bg-green-100', dark: 'bg-green-900/30' },
  purple: {
    bg: 'from-purple-500/80 to-purple-600',
    light: 'bg-purple-100',
    dark: 'bg-purple-900/30',
  },
  red: { bg: 'from-red-500/80 to-red-600', light: 'bg-red-100', dark: 'bg-red-900/30' },
};

export const normalizeGroupName = (name: string): string => {
  const normalized = name.trim();
  const nameMap: Record<string, string> = {
    'KỸ THUẬT': 'Thực tế & Kỹ thuật',
    'NGHIÊN CỨU': 'Nghiên cứu & Khám phá',
    'NGHỆ THUẬT': 'Nghệ thuật & Sáng tạo',
    'XÃ HỘI': 'Xã hội & Cộng đồng',
    'QUẢN LÝ': 'Quản lý & Kinh doanh',
    'NGHIỆP VỤ': 'Nghiệp vụ & Chi tiết',
    'Kỹ thuật': 'Thực tế & Kỹ thuật',
    'Nghiên cứu': 'Nghiên cứu & Khám phá',
    'Nghệ thuật': 'Nghệ thuật & Sáng tạo',
    'Xã hội': 'Xã hội & Cộng đồng',
    'Quản lý': 'Quản lý & Kinh doanh',
    'Nghiệp vụ': 'Nghiệp vụ & Chi tiết',
  };
  return nameMap[normalized] || normalized;
};

// Position mapping for Holland hexagon layout (matching HTML design)
// A=0° (top), S=60°, I=120°, E=180°, R=240°, C=300°
const getGroupPosition = (groupName: string): number => {
  const normalizedName = normalizeGroupName(groupName);
  const positionMap: Record<string, number> = {
    'Nghệ thuật & Sáng tạo': 0, // A - Top
    'Xã hội & Cộng đồng': 1, // S
    'Nghiên cứu & Khám phá': 2, // I
    'Quản lý & Kinh doanh': 3, // E - Bottom
    'Thực tế & Kỹ thuật': 4, // R
    'Nghiệp vụ & Chi tiết': 5, // C
  };
  return positionMap[normalizedName] ?? 0;
};

type BuildVMParams = {
  scores: ScoreEntry[] | undefined;
  groups: CareerOrientationGroupWithQuestions[] | undefined;
  topGroups: TopGroupEntry[];
  fullReportData?: CareerOrientationGroupWithQuestions[] | null;
};

export function buildFullReportViewModel({
  scores = [],
  groups = [],
  topGroups,
  fullReportData,
}: BuildVMParams) {
  const displayGroups = fullReportData || groups;

  // Backend returns scores in 0-8 range (8 questions per group, 1 point per Yes answer)
  // This matches the student app logic exactly
  const riasecScores = scores.map(s => {
    const maxScore = 8; // Each group has 8 questions
    const actualScore = s.score; // Use backend score directly (0-8)
    return {
      ...s,
      actualScore,
      maxScore,
      percentage: (actualScore / maxScore) * 100,
    };
  });

  // Sort by actualScore descending, then by original order for ties
  const sortedScores = [...riasecScores].sort((a, b) => {
    if (b.actualScore !== a.actualScore) {
      return b.actualScore - a.actualScore;
    }
    // For ties, maintain original order based on group position
    const groupA = groups.find(g => g.id === a.id);
    const groupB = groups.find(g => g.id === b.id);
    return (groupA?.order || 0) - (groupB?.order || 0);
  });

  const top3Scores = sortedScores.slice(0, 3);
  const remainingScores = sortedScores.slice(3);

  // Use topGroups from props if available, otherwise calculate from sorted scores
  const top3GroupsForDisplay =
    topGroups.length > 0
      ? topGroups
      : sortedScores.slice(0, 3).map(s => ({ id: s.id, score: s.actualScore, name: s.name }));

  // Build top 3 codes with Holland code mapping
  const top3Codes: TopCode[] = top3GroupsForDisplay.slice(0, 3).map(g => {
    const normalizedName = normalizeGroupName(g.name);
    const codeInfo = hollandCodeMap[normalizedName] || {
      code: '?',
      name: 'Unknown',
      color: 'gray',
      shortName: g.name || 'Unknown',
    };
    return { ...codeInfo, group: g };
  });

  // Build all groups with positions for the compass
  const allGroupsWithPositions: PositionedGroup[] = groups
    .map(g => {
      const score = riasecScores.find(s => s.id === g.id);
      const normalizedName = normalizeGroupName(g.name);
      const codeInfo = hollandCodeMap[normalizedName] || {
        code: '?',
        name: 'Unknown',
        color: 'gray',
        shortName: g.name || 'Unknown',
      };
      const position = getGroupPosition(g.name);
      const isTop3 = top3Scores.some(s => s.id === g.id);
      return {
        ...g,
        ...codeInfo,
        name: g.name, // Restore original name to prevent overwrite by codeInfo.name
        score: score || {
          id: g.id,
          score: 0,
          name: g.name,
          actualScore: 0,
          maxScore: 8,
          percentage: 0,
        },
        position,
        isTop3,
      };
    })
    .sort((a, b) => a.position - b.position);

  return {
    displayGroups,
    top3Codes,
    top3Scores,
    remainingScores,
    allGroupsWithPositions,
  };
}
